import { AriaDescriber, FocusMonitor } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import { Overlay, ScrollDispatcher } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';

import {
  AfterViewInit,
  Directive,
  ElementRef,
  Inject,
  Input,
  NgZone,
  Optional,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MAT_TOOLTIP_SCROLL_STRATEGY,
  MatTooltip,
  MatTooltipDefaultOptions,
} from '@angular/material/tooltip';

export type CustomTooltipPosition =
  | 'left'
  | 'right'
  | 'above'
  | 'below'
  | 'before'
  | 'after';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[customTooltip],[customTooltip-left]',
  exportAs: 'customTooltip',
  standalone: true,
})
export class CustomTooltipDirective
  extends MatTooltip
  implements AfterViewInit
{
  buttonTypes = ['customTooltip', 'customTooltip-left'];

  _messager!: string | string[];
  @Input() set customTooltipLabel(value: string | string[]) {
    if (typeof value === 'string') {
      this.message = value;
      this._messager = value;
    } else {
      this.message = value.join('\n');
      this._messager = value.join('<br/>');
    }
  }

  @Input('customTooltipPosition')
  override get position(): CustomTooltipPosition {
    return super.position as CustomTooltipPosition;
  }
  override set position(value: CustomTooltipPosition) {
    super.position = value;
  }

  @Input('customTooltipClass')
  override get tooltipClass(): string {
    return super.tooltipClass as string;
  }

  override set tooltipClass(value: string) {
    super.tooltipClass = value;
  }

  @Input('customTooltipDisabled')
  override get disabled(): boolean {
    return super.disabled as boolean;
  }
  override set disabled(value: boolean) {
    super.disabled = value;
  }

  _tooltipHideDelay = 0;

  @Input()
  set customTooltipHideDelay(value: number) {
    this._tooltipHideDelay = Number(value);
  }

  _width = 0;
  @Input()
  set customTooltipWidth(value: number | string) {
    this._width = Number(value);
    this._updateTooltipWidth();
  }

  _maxWidth = 0;
  @Input()
  set customTooltipWaxWidth(value: number | string) {
    this._maxWidth = Number(value);
    this._updateTooltipMaxWidth();
  }

  @Input() tooltipPositionText: 'center' | 'left' | 'right' = 'center';

  get activeTooltip(): boolean {
    return (
      !this.customTooltipHideShadow &&
      !this.onlyForOverflow &&
      Boolean(this._tooltipInstance)
    );
  }
  private el: ElementRef;
  @Input() onlyForOverflow = false;
  hasOverflow = false;

  private _customTooltipVisible!: boolean;

  @Input() set customTooltipVisible(value: boolean) {
    this._customTooltipVisible = value;
    value ? this.show() : this.hide();
  }

  @Input() customTooltipHideShadow = false;

  constructor(
    overlay: Overlay,
    elementRef: ElementRef<HTMLElement>,
    scrollDispatcher: ScrollDispatcher,
    viewContainerRef: ViewContainerRef,
    ngZone: NgZone,
    platform: Platform,
    ariaDescriber: AriaDescriber,
    focusMonitor: FocusMonitor,
    @Inject(MAT_TOOLTIP_SCROLL_STRATEGY) scrollStrategy: any,
    @Optional() dir: Directionality,
    @Optional()
    @Inject(MAT_TOOLTIP_DEFAULT_OPTIONS)
    defaultOptions: MatTooltipDefaultOptions,
    @Inject(DOCUMENT) _document: any,
    private renderer: Renderer2
  ) {
    super(
      overlay,
      elementRef,
      scrollDispatcher,
      viewContainerRef,
      ngZone,
      platform,
      ariaDescriber,
      focusMonitor,
      scrollStrategy,
      dir,
      defaultOptions,
      _document
    );
    this.el = elementRef;
  }

  override ngAfterViewInit(): void {
    this._overlayRef = (this as any)._overlayRef;
    super.ngAfterViewInit();
  }

  private _updateTooltipWidth(): void {
    const overlayRef = this._tooltipInstance?._tooltip.nativeElement;
    if (overlayRef) {
      const tooltipContainer = overlayRef.querySelector(
        '.mdc-tooltip__surface'
      );
      if (this._width) {
        this.renderer.setStyle(tooltipContainer, 'width', `${this._width}px`);
        if (this._width > 200) {
          this.renderer.setStyle(
            tooltipContainer,
            'max-width',
            `${this._width}px`
          );
        }
      }
    }
  }

  private _updateTooltipMaxWidth(): void {
    const overlayRef = this._tooltipInstance?._tooltip.nativeElement;
    if (overlayRef) {
      const tooltipContainer = overlayRef.querySelector(
        '.mdc-tooltip__surface'
      );
      if (this._maxWidth) {
        this.renderer.setStyle(
          tooltipContainer,
          'max-width',
          `${this._maxWidth}px`
        );
      }
    }
  }

  private _updateTooltipMessages(): void {
    const overlayRef = this._tooltipInstance?._tooltip.nativeElement;
    if (overlayRef) {
      const tooltip__surface = overlayRef.querySelector(
        '.mdc-tooltip__surface'
      ) as unknown as HTMLElement;
      tooltip__surface.innerHTML = '';
      this.buttonTypes.forEach((type) => {
        if (this._hasHostAttribute(type)) {
          this.renderer.addClass(overlayRef, type);
          this.renderer.setAttribute(overlayRef, 'id', type);
          this.renderer.setStyle(
            tooltip__surface,
            'text-align',
            this.tooltipPositionText
          );
          tooltip__surface.innerHTML += this._messager;
        }
      });
    }
  }

  _hasHostAttribute(...attributes: string[]) {
    return attributes.some((attribute) =>
      this.el.nativeElement.hasAttribute(attribute)
    );
  }

  override show(delay: number = this.showDelay): void {
    if (this.onlyForOverflow && !this.hasOverflow) {
      return;
    }

    if (this._customTooltipVisible === false) {
      return;
    }
    super.show(delay);
    this._updateTooltipMessages();
    this._updateTooltipWidth();
    this._updateTooltipMaxWidth();
  }

  override hide(delay: number = this._tooltipHideDelay): void {
    if (this._customTooltipVisible) {
      this.show();
      return;
    }

    super.hide(delay);
  }
}
